import { useState, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import type { Skill } from '@/types';
import { cn } from '@/lib/utils';
import {
    Lock, CheckCircle, Unlock, ChevronRight,
    Flame, Target, TrendingUp, Zap, Shield, Star
} from 'lucide-react';

import { PageShell } from '@/components/ui/PageShell';

/* ─── Radial Progress Ring ───────────────────────────────────── */
function RadialProgress({ value, size = 160, strokeWidth = 8 }: { value: number; size?: number; strokeWidth?: number }) {
    const [animatedValue, setAnimatedValue] = useState(0);
    useEffect(() => {
        const t = setTimeout(() => setAnimatedValue(value), 200);
        return () => clearTimeout(t);
    }, [value]);

    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (animatedValue / 100) * circumference;

    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90 overflow-visible">
                {/* Track */}
                <circle cx={size / 2} cy={size / 2} r={radius} stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} fill="transparent" />
                {/* Glow Track */}
                <circle cx={size / 2} cy={size / 2} r={radius}
                    stroke="url(#goldGradient)" strokeWidth={strokeWidth} fill="transparent"
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                    className="transition-all duration-[1500ms] ease-out"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.6))' }}
                />
                <defs>
                    <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFD700" />
                        <stop offset="50%" stopColor="#C9A227" />
                        <stop offset="100%" stopColor="#FFD700" />
                    </linearGradient>
                </defs>
            </svg>
            {/* Center Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-black text-white leading-none drop-shadow-lg">{animatedValue}%</span>
                <span className="text-[10px] font-bold text-gold-400 uppercase tracking-[0.2em] mt-1">Mastery</span>
            </div>
        </div>
    );
}

/* ─── Stat Pill ──────────────────────────────────────────────── */
function StatPill({ icon: Icon, label, value, color }: { icon: any; label: string; value: number | string; color: string }) {
    return (
        <div className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-white/5 min-w-[80px] backdrop-blur-sm">
            <Icon size={18} className={color} />
            <span className="text-lg font-black text-white leading-none">{value}</span>
            <span className="text-[9px] font-bold text-text-muted uppercase tracking-wider">{label}</span>
        </div>
    );
}

/* ─── Tier Divider ───────────────────────────────────────────── */
function TierHeader({ name, level, masteredCount, totalCount }: { name: string; level: number; masteredCount: number; totalCount: number }) {
    const tierIcons = [Shield, Target, Flame, Zap, Star];
    const TierIcon = tierIcons[Math.min(level - 1, tierIcons.length - 1)];
    const pct = totalCount > 0 ? Math.round((masteredCount / totalCount) * 100) : 0;

    return (
        <div className="flex items-center gap-4 py-1">
            <div className="flex items-center gap-3 flex-1">
                <div className={cn(
                    "w-9 h-9 rounded-xl flex items-center justify-center border",
                    pct === 100
                        ? "bg-gold-500/20 border-gold-500/40 text-gold-400"
                        : "bg-white/5 border-white/10 text-text-muted"
                )}>
                    {TierIcon && <TierIcon size={18} />}
                </div>
                <div>
                    <h3 className="text-sm font-black text-white uppercase tracking-wide">{name}</h3>
                    <p className="text-[10px] text-text-muted font-bold">
                        {masteredCount}/{totalCount} Mastered
                    </p>
                </div>
            </div>
            {/* Mini progress bar */}
            <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                    className={cn(
                        "h-full rounded-full transition-all duration-1000 ease-out",
                        pct === 100 ? "bg-gold-500 shadow-[0_0_6px_rgba(212,175,55,0.5)]" : "bg-blue-500/60"
                    )}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

/* ─── Skill Card (Premium) ───────────────────────────────────── */
function SkillCard({ skill, status }: { skill: Skill; status: 'locked' | 'available' | 'mastered' }) {
    return (
        <button
            disabled={status === 'locked'}
            className={cn(
                "w-full text-left p-4 rounded-2xl border transition-all duration-300 relative group overflow-hidden",
                status === 'mastered'
                    ? "bg-gradient-to-br from-gold-500/[0.08] to-transparent border-gold-500/20 hover:border-gold-500/40 shadow-[0_0_30px_rgba(212,175,55,0.05)]"
                    : status === 'available'
                        ? "bg-bg-elevated/80 border-white/10 hover:border-blue-500/40 hover:bg-bg-surface active:scale-[0.98] shadow-card"
                        : "bg-bg-surface/30 border-white/[0.03] opacity-50 cursor-not-allowed"
            )}
        >
            <div className="flex items-center justify-between z-10 relative">
                <div className="flex items-center gap-4">
                    <div className={cn(
                        "w-11 h-11 rounded-xl flex items-center justify-center border transition-all duration-300",
                        status === 'mastered'
                            ? "bg-gold-500/15 border-gold-500/30 text-gold-400 shadow-[0_0_15px_rgba(212,175,55,0.15)]"
                            : status === 'available'
                                ? "bg-blue-500/10 border-blue-500/20 text-blue-400 group-hover:bg-blue-500/20 group-hover:border-blue-500/40"
                                : "bg-black/20 border-white/5 text-white/15"
                    )}>
                        {status === 'mastered' && <CheckCircle className="w-5 h-5" />}
                        {status === 'available' && <Unlock className="w-5 h-5" />}
                        {status === 'locked' && <Lock className="w-5 h-5" />}
                    </div>

                    <div className="flex-1 min-w-0">
                        <h4 className={cn(
                            "font-bold text-sm leading-tight",
                            status === 'mastered' ? "text-gold-200"
                                : status === 'available' ? "text-text-primary group-hover:text-white"
                                    : "text-text-muted/60"
                        )}>
                            {skill.name}
                        </h4>
                        {status !== 'locked' && (
                            <p className="text-[11px] text-text-muted mt-0.5 line-clamp-1 leading-snug">
                                {skill.description}
                            </p>
                        )}
                        {status === 'locked' && (
                            <p className="text-[10px] text-text-muted/40 mt-0.5 uppercase tracking-wide font-bold flex items-center gap-1">
                                <Lock size={8} /> Prerequisites Required
                            </p>
                        )}
                    </div>
                </div>

                {status === 'mastered' && (
                    <div className="px-2.5 py-1 rounded-full bg-gold-500/15 border border-gold-500/25 text-gold-400 text-[9px] font-black uppercase tracking-wider flex-shrink-0">
                        Mastered
                    </div>
                )}
                {status === 'available' && (
                    <ChevronRight className="w-5 h-5 text-white/15 group-hover:text-blue-400 transition-all group-hover:translate-x-0.5 flex-shrink-0" />
                )}
            </div>

            {/* Hover shimmer for available skills */}
            {status === 'available' && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            )}
            {/* Gold shimmer for mastered skills */}
            {status === 'mastered' && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold-500/[0.04] to-transparent pointer-events-none" />
            )}
        </button>
    );
}

/* ─── Main Component ─────────────────────────────────────────── */
export function SkillProgression() {
    // 1. Data Fetching
    const pillars = useLiveQuery(() => db.pillars.orderBy('order').toArray()) ?? [];
    const tiers = useLiveQuery(() => db.tiers.toArray()) ?? [];
    const skills = useLiveQuery(() => db.skills.toArray()) ?? [];
    const progress = useLiveQuery(() => db.skillProgress.toArray()) ?? [];

    // 2. State
    const [activePillarId, setActivePillarId] = useState<string>('');

    useEffect(() => {
        if (!activePillarId && pillars.length > 0) {
            setActivePillarId(pillars[0]?.id ?? '');
        }
    }, [activePillarId, pillars]);

    // 3. Derived Data
    const activePillar = pillars.find(p => p.id === activePillarId);
    const activeTiers = tiers
        .filter(t => t.pillarId === activePillarId)
        .sort((a, b) => a.level - b.level);

    const getSkillStatus = (skillId: string) => {
        const p = progress.find(kp => kp.skillId === skillId);
        if (p?.status === 'mastered') return 'mastered';

        const skill = skills.find(s => s.id === skillId);
        if (!skill) return 'locked';
        if (skill.prerequisiteIds.length === 0) return 'available';

        const allPrereqsMet = skill.prerequisiteIds.every(preId => {
            const preProg = progress.find(pr => pr.skillId === preId);
            return preProg?.status === 'mastered';
        });
        return allPrereqsMet ? 'available' : 'locked';
    };

    const masteredCount = progress.filter(p => p.status === 'mastered').length;
    const totalSkills = skills.length;
    const completionPercentage = totalSkills > 0 ? Math.round((masteredCount / totalSkills) * 100) : 0;
    const availableCount = skills.filter(s => getSkillStatus(s.id) === 'available').length;

    return (
        <PageShell
            title="Progression"
            subtitle="Road to Elite"
            right={
                <div className="flex gap-2">
                    <StatPill icon={CheckCircle} label="Mastered" value={masteredCount} color="text-gold-400" />
                    <StatPill icon={Unlock} label="Available" value={availableCount} color="text-blue-400" />
                </div>
            }
        >
            <div className="animate-fade-in space-y-6">

                {/* ─── Hero Section ────────────────────────────────── */}
                <div className="relative overflow-hidden mb-6 flex flex-col items-center">
                    <RadialProgress value={completionPercentage} />
                </div>

                {/* ─── Pillar Selector ─────────────────────────────── */}
                <div className="px-6 mt-4">
                    <div className="flex gap-2 overflow-x-auto hide-scrollbar py-1">
                        {pillars.map(pillar => {
                            const isActive = pillar.id === activePillarId;
                            const pillarSkills = skills.filter(s => {
                                const t = tiers.find(t => t.id === s.tierId);
                                return t?.pillarId === pillar.id;
                            });
                            const pillarMastered = pillarSkills.filter(s => {
                                const p = progress.find(pr => pr.skillId === s.id);
                                return p?.status === 'mastered';
                            }).length;
                            const pillarPct = pillarSkills.length > 0 ? Math.round((pillarMastered / pillarSkills.length) * 100) : 0;

                            return (
                                <button
                                    key={pillar.id}
                                    onClick={() => setActivePillarId(pillar.id)}
                                    className={cn(
                                        "relative flex flex-col items-center gap-1.5 px-5 py-3 rounded-2xl text-center whitespace-nowrap transition-all duration-300 border min-w-[100px]",
                                        isActive
                                            ? "bg-gold-500/15 border-gold-500/30 shadow-[0_0_20px_rgba(212,175,55,0.1)] scale-[1.02]"
                                            : "bg-bg-elevated/50 border-white/5 hover:bg-white/5 hover:border-white/10"
                                    )}
                                >
                                    <span className={cn(
                                        "text-[11px] font-black uppercase tracking-wider",
                                        isActive ? "text-gold-400" : "text-text-muted"
                                    )}>
                                        {pillar.name}
                                    </span>
                                    <span className={cn(
                                        "text-[9px] font-bold",
                                        isActive ? "text-gold-500/60" : "text-text-muted/40"
                                    )}>
                                        {pillarPct}%
                                    </span>
                                    {/* Active indicator dot */}
                                    {isActive && (
                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-gold-500 shadow-[0_0_6px_rgba(212,175,55,0.8)]" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ─── Skills Content ──────────────────────────────── */}
                <div className="px-6 mt-8 space-y-6 min-h-[400px]">
                    {activePillar && (
                        <div key={activePillarId} className="animate-fade-in-up space-y-6">
                            {/* Pillar Description Card */}
                            <div className="p-5 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/5 blur-3xl rounded-full" />
                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-2">
                                        <TrendingUp size={18} className="text-gold-500" />
                                        <h3 className="text-base font-black text-white uppercase tracking-wide">{activePillar.name}</h3>
                                    </div>
                                    <p className="text-sm text-text-muted leading-relaxed">{activePillar.description}</p>
                                </div>
                            </div>

                            {/* Tier Groups */}
                            {activeTiers.map(tier => {
                                const tierSkills = skills.filter(s => s.tierId === tier.id);
                                if (tierSkills.length === 0) return null;

                                const tierMastered = tierSkills.filter(s => {
                                    const p = progress.find(pr => pr.skillId === s.id);
                                    return p?.status === 'mastered';
                                }).length;

                                return (
                                    <div key={tier.id} className="space-y-3">
                                        <TierHeader
                                            name={tier.name}
                                            level={tier.level}
                                            masteredCount={tierMastered}
                                            totalCount={tierSkills.length}
                                        />

                                        <div className="grid gap-2.5 pl-3 border-l-2 border-white/5 ml-4">
                                            {tierSkills.map(skill => (
                                                <SkillCard
                                                    key={skill.id}
                                                    skill={skill}
                                                    status={getSkillStatus(skill.id)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </PageShell>
    );
}
